import { createSelector } from 'reselect';
import {
  getSummary,
  getAnalysis,
  getFormatting,
} from '@/pages/CompanyReport/redux/selectors';
import { getAveragesSelector } from '@ducks/averages/selectors';
import transformToTimeSeries, {
  seriesArraySortDesending,
} from '@/pages/CompanyReport/utils/tranformToTimeSeries';
import { minBy, maxBy } from 'lodash';

interface StabilityAndGrowthOfPayments {
  DPS: number[][];
  DPSMax: number;
  DPSMin: number;
  EPS: number[][];
  EPSMax: number;
  EPSMin: number;
  currencyISO: string;
  dayZero: number;
  dividendYield: number[][];
  dividendYieldMax: number;
  dividendYieldMin: number;
  reportingUnit: number;
}

const filterForFuture = (data: number[][]) =>
  data.filter((estimate) => estimate.length > 1 && estimate[0] > Date.now());

export const getStabilityAndGrowthOfPaymentsSelector = createSelector(
  [getAnalysis, getFormatting],
  (analysis, formatting): StabilityAndGrowthOfPayments => {
    //TODO: once future dividend data and EPS is surfaced to gql
    //move this selector logic to useDividendData() hook
    const {
      legacyChartData: {
        historicalDividendPayments,
        mergedFutureDividendsPerShare,
        mergedFutureYield,
        uncappedPastEps,
      },
    } = analysis;
    const DPS = filterForFuture(mergedFutureDividendsPerShare).map((n) => [
      n[0],
      n[1] / formatting.secondaryListingExchangeRate,
    ]);

    const DPSMax = maxBy(DPS, (n) => n[1]);
    const DPSMin = minBy(DPS, (n) => n[1]);

    const dividendYield = filterForFuture(mergedFutureYield).map((n) => [
      n[0],
      n[1],
    ]);
    const dividendYieldMax = maxBy(dividendYield, (n) => n[1]);
    const dividendYieldMin = minBy(dividendYield, (n) => n[1]);

    const EPS = uncappedPastEps.map((n) => [
      n[0],
      n[1] / formatting.secondaryListingExchangeRate,
    ]);
    const EPSMin = minBy(EPS, (n) => n[1]);
    const EPSMax = maxBy(EPS, (n) => n[1]);

    let dayZero = maxBy(historicalDividendPayments, (n) => n[0]);

    if (historicalDividendPayments.length !== 0) {
      dayZero = maxBy(historicalDividendPayments, (n) => n[0]);
    } else {
      dayZero = minBy(mergedFutureDividendsPerShare, (n) => n[0]);
    }

    return {
      DPS,
      DPSMax: DPSMax ? DPSMax[1] : 0,
      DPSMin: DPSMin ? DPSMin[1] : 0,
      EPS,
      EPSMax: EPSMax ? EPSMax[1] : 0,
      EPSMin: EPSMin ? EPSMin[1] : 0,
      currencyISO: formatting.tradingItemCurrencyISO,
      dayZero: dayZero ? dayZero[0] : new Date().getTime(),
      dividendYield,
      dividendYieldMax: dividendYieldMax ? dividendYieldMax[1] : 0,
      dividendYieldMin: dividendYieldMin ? dividendYieldMin[1] : 0,
      reportingUnit: formatting.reportingUnit,
    };
  }
);
interface StabilityAndGrowthOfPaymentsRawData {
  uniqueSymbol: string;
  analystCount: number;
  reportingCurrencyISO: string;
  industryCountryName: string;
  industrySecondaryName: string;
  industryAverageDividendYield: number;
  industryAverageDividendYieldCount: number;
  marketCountryName: string;
  marketDividendYield: number;
  marketDividendYieldCount: number;
  marketDividendYield10thPercentile: number;
  marketDividendYield25thPercentile: number;
  marketDividendYield75thPercentile: number;
  futureDividendEstimates: { dateKey: number; data: number[] }[];
  pastAnnualizedDividendPayouts: {
    dateKey: number;
    data: number[];
  }[];
}

export const getStabilityAndGrowthOfPaymentsRawDataSelector = createSelector(
  [getSummary, getAnalysis, getFormatting, getAveragesSelector],
  (
    summary,
    analysis,
    formatting,
    averages
  ): StabilityAndGrowthOfPaymentsRawData => {
    const { getIndustryAverage, getMarketAverage } = averages;
    const { legacyChartData } = analysis;

    const industryDividendYield = getIndustryAverage('dividendYield');

    const marketDividendYield = getMarketAverage('dividendYield');

    return {
      uniqueSymbol: summary.uniqueSymbol,
      analystCount: summary.analystCount,
      reportingCurrencyISO: formatting.reportingCurrencyISO,
      industryCountryName: industryDividendYield.source,
      industrySecondaryName: summary.industryData.secondaryName,
      industryAverageDividendYield: industryDividendYield.value,
      industryAverageDividendYieldCount: industryDividendYield.count,
      marketCountryName: marketDividendYield.source,
      marketDividendYield: marketDividendYield.value,
      marketDividendYieldCount: marketDividendYield.count,
      marketDividendYield10thPercentile:
        getMarketAverage('dividendYieldP10').value,
      marketDividendYield25thPercentile:
        getMarketAverage('dividendYieldP25').value,
      marketDividendYield75thPercentile:
        getMarketAverage('dividendYieldP75').value,
      futureDividendEstimates: transformToTimeSeries([
        filterForFuture(legacyChartData.mergedFutureDividendsPerShare).map(
          (n) => [n[0], n[1] / formatting.secondaryListingExchangeRate]
        ),
        filterForFuture(legacyChartData.mergedFutureYield).map((n) => [
          n[0],
          n[1] * 100,
        ]),
      ]).sort(seriesArraySortDesending),

      pastAnnualizedDividendPayouts: transformToTimeSeries([
        legacyChartData.historicalDividendPayments,
        legacyChartData.historicalDividendYield.map((n) => [n[0], n[1] * 100]),
      ]).sort(seriesArraySortDesending),
    };
  }
);
